import {throwError as observableThrowError, Observable, ReplaySubject, of} from 'rxjs';
import {Injectable } from '@angular/core';
import {CacheService} from './cache.service';
import {SCMProject, SCMProjectUpdateModel, SCMProjectWriteModel} from '../models/SCMProject';
import {SCMProjectPermission} from '../models/SCMProjectPermission';
import {environment} from '../../environments/environment';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {catchError, map, mapTo, tap} from 'rxjs/operators';
import {HttpService} from './http.service';

@Injectable()
export class ProjectService {

    private _customerProjects$: ReplaySubject<Array<SCMProject>> = new ReplaySubject(1);
    customerProjects: Array<SCMProject>;

    get customerProjects$(): Observable<Array<SCMProject>> {
        return this._customerProjects$.asObservable();
    }

    constructor(
        private httpService: HttpService,
        private cacheService: CacheService
    ) {

        this.customerProjects$.subscribe(
            projects => {
                this.customerProjects = projects;
            }
        )
    }

    getCustomerProjects(skipCache: boolean = false): Observable<Array<SCMProject>> {
        if (!skipCache && this.cacheService.hasItem('currentCustomerProjects', 0)) {
            return of(this.cacheService.getItem('currentCustomerProjects', 0));
        } else {
            return this.fetchCustomerProjects();
        }
    }

    private fetchCustomerProjects(): Observable<Array<SCMProject>> {

        let getParams: HttpParams = new HttpParams();

        getParams = getParams.set('getAll', 'true');
        getParams = getParams.append('status', 'ACTIVE');
        getParams = getParams.append('status', 'DISABLED');

        return this.httpService.get<Array<SCMProject>>(environment.apiV1BaseUrl + '/supplychains/projects', { params: getParams }).pipe(
            tap((customerProjects: Array<SCMProject>) => this.cacheService.setItem('currentCustomerProjects', 0, customerProjects)),
            tap((customerProjects: Array<SCMProject>) => this._customerProjects$.next(customerProjects))
        )

    }

    getProjectPermissions(projectId: number, skipCache: boolean = false): Observable<Array<number>> {

        if (!skipCache && this.cacheService.hasItem('projectPermissions', projectId)) {
            return of(this.cacheService.getItem('projectPermissions', projectId));
        } else {
            return this.fetchProjectPermissions(projectId);
        }
    }

    private fetchProjectPermissions(projectId: number): Observable<Array<number>> {

        return this.httpService.get<Array<SCMProjectPermission>>(environment.apiV1BaseUrl + '/supplychains/projects/' + projectId + '/permissions').pipe(
            map((permissions: Array<SCMProjectPermission>) => permissions.map(p => p.userId)),
            tap((permissions) => this.cacheService.setItem('projectPermissions', projectId, permissions))
        );

    }

    addUserToProject(userId: number, projectId: number): Observable<boolean> {

        return this.httpService.post(
            environment.apiV1BaseUrl + '/supplychains/projects/' + projectId + '/permissions',
            { userId: userId },
            { observe: 'response' }
        ).pipe(
            tap((response: HttpResponse<any>) => {
                if (response.ok) {
                    this.cacheService.clearItem('projectPermissions', projectId);
                }
            }),
            mapTo(true),
            catchError((err: Error) => {
                if (err.message && err.message.includes('HTTP 400')) {
                    // This happens when a user already had access to the project
                    return of(false);
                }
                throw err;
            })
        );

    }

    removeUserFromProject(userId: number, projectId: number): Observable<boolean> {

        return this.httpService.delete(
            environment.apiV1BaseUrl + '/supplychains/projects/' + projectId + '/permissions/' + userId,
            { observe: 'response' }
        ).pipe(
            map((response: HttpResponse<any>) => {

                if (response.ok) {
                    this.cacheService.clearItem('projectPermissions', projectId);
                    return true;
                }

                return false;
            }),
            catchError((err: Error) => {
                if (err.message && err.message.includes('HTTP 404')) {
                    // This happens when a user did not have access to the project
                    return of(false);
                }
                throw err;
            })
        );

    }

    createProject(project: SCMProjectWriteModel): Observable<number> {

        return this.httpService.post(
            environment.apiV1BaseUrl + '/supplychains/projects',
            project,
            { observe: 'response' }
        ).pipe(
            map((response: HttpResponse<any>) => {

                const id = HttpService.extractIdFromLocationHeader(response) as number;

                // this.cacheService.setItem('projects', id, project);
                this.cacheService.clearNamespace('currentCustomerProjects');

                return id;
            })
        );
    }

    updateProject(project: SCMProjectUpdateModel): Observable<boolean> {

        return this.httpService.put(
            environment.apiV1BaseUrl + '/supplychains/projects/' + project.id,
            project,
            { observe: 'response' }
        ).pipe(
            tap((response: HttpResponse<any>) => {
                if (response.ok) {
                    this.cacheService.setItem('projects', project.id, project);
                }
            }),
            mapTo(true)
        );
    }


    getProject(projectId: number, skipCache: boolean = false): Observable<SCMProject> {

        if (!skipCache && this.cacheService.hasItem('projects', projectId)) {
            return of(this.cacheService.getItem('projects', projectId));
        }

        return this.httpService.get<SCMProject>(environment.apiV1BaseUrl + '/supplychains/projects/' + projectId).pipe(
            tap((project: SCMProject) => this.cacheService.setItem('projects', project.id, project))
        );

    }
}
